import React, { useState } from 'react';
import CommonLayout from '../layouts/CommonLayout';
import { sendVerificationCode, verifyVerificationCode, resetPassword } from '../../api/AuthApi';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from '../layouts/AlertModal'; // AlertModal import

const PasswordResetPage = () => {
  const navigate = useNavigate();

  const [resetForm, setResetForm] = useState({
    phone: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  // 모달 상태 관리
  const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

  const getErrorMessage = (error) => {
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  };

  const handleSendCode = async () => {
    try {
      await sendVerificationCode(resetForm.phone);
      setAlertState({ isVisible: true, message: '인증번호가 발송되었습니다.' });
      setIsCodeSent(true);
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) });
    }
  };

  const handleVerifyCode = async () => {
    try {
      await verifyVerificationCode(resetForm.phone, resetForm.verificationCode);
      setAlertState({ isVisible: true, message: '인증이 완료되었습니다.' });
      setIsPhoneVerified(true);
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) });
    }
  };

  const handleResetPassword = async () => {
    if (!isPhoneVerified) {
      setAlertState({ isVisible: true, message: '휴대폰 번호 인증을 완료해주세요' });
      return;
    }

    if (resetForm.newPassword.length < 6) {
      setAlertState({ isVisible: true, message: '비밀번호는 6자 이상이어야 합니다.' });
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setAlertState({ isVisible: true, message: '비밀번호가 일치하지 않습니다.' });
      return;
    }

    try {
      await resetPassword(resetForm.phone, resetForm.newPassword);
      setAlertState({
        isVisible: true,
        message: '비밀번호가 재설정되었습니다.',
        callback: () => navigate('/')
      });
    } catch (error) {
      setAlertState({ isVisible: true, message: getErrorMessage(error) });
    }
  };

  return (
      <CommonLayout title="비밀번호 재설정">
        <AlertModal
            isVisible={alertState.isVisible}
            message={alertState.message}
            callback={alertState.callback}
            onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
        />
        <div className="space-y-4 p-4">
          {/* 휴대폰 번호 입력 필드 */}
          <div className="flex items-center space-x-2">
            <div className="text-sm font-semibold">휴대폰 번호 인증</div>
            {isPhoneVerified && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600 font-medium">인증완료</span>
                </div>
            )}
          </div>
          <div className="flex space-x-2">
            <input
                type="text"
                placeholder="휴대폰 번호 (- 없이 입력)"
                value={resetForm.phone}
                onChange={(e) => setResetForm({ ...resetForm, phone: e.target.value })}
                className={`${isPhoneVerified ? 'w-full' : 'w-3/5'} px-2 py-3 border rounded-lg ${
                    isPhoneVerified ? 'border-green-300 bg-green-50' : 'border-gray-300'
                }`}
                minLength="11"
                maxLength="11"
                disabled={isPhoneVerified}
            />
            {!isPhoneVerified && (
                <button
                    onClick={handleSendCode}
                    className="w-2/5 px-2 py-3 bg-gray-200 text-gray-700 rounded-lg whitespace-nowrap"
                >
                  {isCodeSent ? '인증번호 재전송' : '인증번호 전송'}
                </button>
            )}
          </div>

          {/* 인증번호 입력 필드 */}
          <div className="flex space-x-2">
            <input
                type="text"
                placeholder="인증번호 6자리"
                value={resetForm.verificationCode}
                onChange={(e) => setResetForm({ ...resetForm, verificationCode: e.target.value })}
                className={`${isPhoneVerified ? 'w-full border-green-300 bg-green-50' : 'w-3/5 border-gray-300'} px-3 py-3 border rounded-lg`}
                maxLength="6"
                disabled={isPhoneVerified}
            />
            {!isPhoneVerified && (
                <button
                    onClick={handleVerifyCode}
                    className="w-2/5 px-1 py-3 bg-gray-200 text-gray-700 rounded-lg whitespace-nowrap"
                >
                  인증번호 확인
                </button>
            )}
          </div>

          {/* 새로운 비밀번호 입력 필드 */}
          <div className="text-sm font-semibold">새로운 비밀번호</div>
          <div>
            <input
                type="password"
                placeholder="새로운 비밀번호 (6자 이상, 16자 이하)"
                value={resetForm.newPassword}
                onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                minLength="6"
                maxLength="16"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            />
          </div>

          {/* 비밀번호 확인 필드 */}
          <div className="text-sm font-semibold">비밀번호 확인</div>
          <div>
            <input
                type="password"
                placeholder="비밀번호 확인"
                value={resetForm.confirmPassword}
                onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                minLength="6"
                maxLength="16"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            />
          </div>

          <button
              onClick={handleResetPassword}
              className="w-full py-3 bg-amber-500 text-white rounded-lg"
          >
            비밀번호 재설정
          </button>
        </div>
      </CommonLayout>
  );
};

export default PasswordResetPage;
